import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Modal, Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Authapi from '../Services/Authapi';
import ls from "local-storage";

const AuthCheckComponent = (props) => {
    const [open, setOpen] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const getLoginRedirectPath = useCallback(() => {
        try {
            const storedRoute = window.sessionStorage.getItem('postLogoutLoginRoute');
            return ['/login'].includes(storedRoute)
                ? storedRoute
                : '/login';
        } catch (error) {
            return '/login';
        }
    }, []);
    const isLoginRoute =
        typeof window !== 'undefined' &&
        window.location &&
        (
            window.location.pathname === '/login'
        );
    const refreshInProgressRef = useRef(false);

    const persistNewToken = useCallback((newToken) => {
        localStorage.setItem('authToken', newToken.access_token);
        localStorage.setItem('roles', newToken.roles);
        localStorage.setItem('tokenRefreshed', 'true');
    }, []);

    const tryRefreshToken = useCallback(async () => {
        if (refreshInProgressRef.current) return true; // prevent duplicate attempts
        if (!ls("user")?.id) return false;
        try {
            refreshInProgressRef.current = true;
            const formData = { user_id: ls("user").id };
            const newToken = await Authapi.refreshToken(formData);
            if (newToken && newToken.access_token) {
                persistNewToken(newToken);
                return true;
            }
            return false;
        } catch (error) {
            return false;
        } finally {
            refreshInProgressRef.current = false;
        }
    }, [persistNewToken]);

    useEffect(() => {
        if (isLoginRoute) return;
        // If app thinks token is false, first try to refresh silently.
        const maybeRefresh = async () => {
            if (!isChecking && props && props.authToken === false) {
                const ok = await tryRefreshToken();
                if (ok) {
                    // Token refreshed successfully; reload once to sync app state
                    window.location.reload();
                    return;
                }
                // Refresh failed => redirect to login immediately
                try {
                    window.location.replace(getLoginRedirectPath());
                } catch (e) {
                    window.location.href = getLoginRedirectPath();
                }
            }
        };
        maybeRefresh();
    }, [props, isChecking, isLoginRoute, tryRefreshToken, getLoginRedirectPath]);

    const handleRefresh = async () => {
        setOpen(false);

        try {
            const ok = await tryRefreshToken();
            if (ok) {
                window.location.reload();
                return;
            }
        } catch (error) {
            console.error('Error refreshing token:', error);
        }
        // If still not ok, redirect to login
        try {
            window.location.replace(getLoginRedirectPath());
        } catch (e) {
            window.location.href = getLoginRedirectPath();
        }
    };

    useEffect(() => {
        // Perform a silent token refresh on initial mount so that
        // a direct URL navigation doesn't immediately show session expired.
        const silentRefresh = async () => {
            try {
                if (isLoginRoute) {
                    setIsChecking(false);
                    return;
                }
                let formData = {
                    user_id: ls("user")?.id
                };
                if (!formData.user_id) {
                    setIsChecking(false);
                    // No user in storage and not on login → go to login
                    try {
                        window.location.replace(getLoginRedirectPath());
                    } catch (e) {
                        window.location.href = getLoginRedirectPath();
                    }
                    return;
                }
                const newToken = await Authapi.refreshToken(formData);
                if (newToken && newToken.access_token) {
                    persistNewToken(newToken);
                }
            } catch (error) {
                // Silent failure; UI will try again via the effect above
            } finally {
                setIsChecking(false);
            }
        };
        silentRefresh();
    }, [isLoginRoute, persistNewToken, getLoginRedirectPath]);

    // Deprecated: the eager refresh on mount caused premature session-expired modal.
    // Keeping the function removed to rely on the silent refresh above.

    return (
        <div>
            <Modal
                open={open && !isLoginRoute}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                        textAlign: 'center',
                        border: 'none',
                        outline: 'none',
                    }}
                >
                    <ErrorOutlineIcon sx={{ fontSize: 60, color: '#f44336' }} />
                    <Typography id="modal-title" variant="h5" component="h2" sx={{ color: '#f44336', mt: 2, fontWeight: 'bold' }}>
                        Session Expired
                    </Typography>
                    <Typography id="modal-description" sx={{ mt: 2, color: 'text.secondary' }}>
                        Your session has expired due to inactivity. Please refresh to continue.
                    </Typography>
                    <Button variant="contained" color="primary" onClick={handleRefresh} sx={{ mt: 3, bgcolor: '#FF0000', '&:hover': { bgcolor: '#FF0000' } }}>
                        Refresh Now
                    </Button>
                </Box>
            </Modal>
        </div>
    );
};

export default AuthCheckComponent;
